import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { showSuccess, showError } from "../../utils/alerts";
import { registerUser, clearError } from '../../store/slices/authSlice';

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, isAuthenticated } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  useEffect(() => {
    if (!isLoading && !error && !isAuthenticated && formData.name && formData.email) {
      showSuccess("Registration Successful", "Please log in to continue.");
      navigate('/login');
    }

    if (error) {
      showError("Registration Failed", error);
    }
  }, [isLoading, error, isAuthenticated]);

  useEffect(() => {
    // Clear any existing errors when component mounts
    dispatch(clearError());
  }, [dispatch]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    // Clear validation errors when user starts typing
    if (validationErrors[e.target.name]) {
      setValidationErrors({
        ...validationErrors,
        [e.target.name]: '',
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (formData.password !== formData.password_confirmation) {
      errors.password_confirmation = 'Passwords do not match';
    }

    if (!agreeToTerms) {
      errors.terms = 'You must agree to the privacy policy & terms';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      dispatch(registerUser(formData));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="authentication-wrapper authentication-cover">
      {/* Logo */}
      {/* /Logo */}

      <div className="authentication-inner row m-0">
        {/* Left Section */}
        <div className="d-none d-lg-flex col-lg-7 col-xl-8 align-items-center justify-content-center p-12 pb-2">
          <img
            src="/assets/img/illustrations/auth-register-illustration-light.png"
            className="auth-cover-illustration w-100"
            alt="auth-illustration"
            data-app-light-img="illustrations/auth-register-illustration-light.png"
            data-app-dark-img="illustrations/auth-register-illustration-dark.png"
          />
          <img
            src="/assets/img/illustrations/auth-cover-register-mask-light.png"
            className="authentication-image"
            alt="mask"
            data-app-light-img="illustrations/auth-cover-register-mask-light.png"
            data-app-dark-img="illustrations/auth-cover-register-mask-dark.png"
          />
        </div>
        {/* /Left Section */}

        {/* Register Form */}
        <div className="d-flex col-12 col-lg-5 col-xl-4 align-items-center authentication-bg position-relative py-sm-12 px-12 py-6">
          <div className="w-px-400 mx-auto pt-5 pt-lg-0">
            <div className="text-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/logo-samplee.png`}
                alt="Recovery House Logo"
                style={{ width: "200px", height: "auto" }}
                className="mb-3"
              />
              <h4 className="mb-1">Adventure starts here 🚀</h4>
              <p className="mb-5">Make your app management easy and fun!</p>
            </div>


            {/* {error && <div className="alert alert-danger">{error}</div>} */}

            <form id="formAuthentication" className="mb-5" onSubmit={handleSubmit}>
              {/* Name Field */}
              <div className="form-floating form-floating-outline mb-4">
                <input
                  type="text"
                  className={`form-control ${validationErrors.name ? 'is-invalid' : ''}`}
                  id="name"
                  name="name"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isLoading}
                  autoFocus
                />
                <label htmlFor="name">Full Name</label>
                {validationErrors.name && (
                  <div className="invalid-feedback d-block">{validationErrors.name}</div>
                )}
              </div>

              {/* Email Field */}
              <div className="form-floating form-floating-outline mb-4">
                <input
                  type="text"
                  className={`form-control ${validationErrors.email ? 'is-invalid' : ''}`}
                  id="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                <label htmlFor="email">Email</label>
                {validationErrors.email && (
                  <div className="invalid-feedback d-block">{validationErrors.email}</div>
                )}
              </div>

              {/* Password Field */}
              <div className="mb-4">
                <div className="form-password-toggle">
                  <div className="input-group input-group-merge">
                    <div className="form-floating form-floating-outline">
                      <input
                        type={showPassword ? "text" : "password"}
                        className={`form-control ${validationErrors.password ? 'is-invalid' : ''}`}
                        id="password"
                        name="password"
                        placeholder="••••••••••••"
                        value={formData.password}
                        onChange={handleChange}
                        disabled={isLoading}
                        aria-describedby="password"
                      />
                      <label htmlFor="password">Password</label>
                    </div>
                    <span
                      className="input-group-text cursor-pointer"
                      onClick={togglePasswordVisibility}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className={`ri-eye${showPassword ? '-line' : '-off-line'}`}></i>
                    </span>
                  </div>
                  {validationErrors.password && (
                    <div className="invalid-feedback d-block">{validationErrors.password}</div>
                  )}
                </div>
              </div>

              {/* Confirm Password Field */}
              <div className="mb-4">
                <div className="form-password-toggle">
                  <div className="input-group input-group-merge">
                    <div className="form-floating form-floating-outline">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        className={`form-control ${validationErrors.password_confirmation ? 'is-invalid' : ''}`}
                        id="password_confirmation"
                        name="password_confirmation"
                        placeholder="••••••••••••"
                        value={formData.password_confirmation}
                        onChange={handleChange}
                        disabled={isLoading}
                        aria-describedby="password_confirmation"
                      />
                      <label htmlFor="password_confirmation">Confirm Password</label>
                    </div>
                    <span
                      className="input-group-text cursor-pointer"
                      onClick={toggleConfirmPasswordVisibility}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className={`ri-eye${showConfirmPassword ? '-line' : '-off-line'}`}></i>
                    </span>
                  </div>
                  {validationErrors.password_confirmation && (
                    <div className="invalid-feedback d-block">{validationErrors.password_confirmation}</div>
                  )}
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="mb-5">
                <div className="form-check mt-2">
                  <input
                    className={`form-check-input ${validationErrors.terms ? 'is-invalid' : ''}`}
                    type="checkbox"
                    id="terms-conditions"
                    name="terms"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    disabled={isLoading}
                  />
                  <label className="form-check-label" htmlFor="terms-conditions">
                    I agree to
                    <a href="javascript:void(0);"> privacy policy & terms</a>
                  </label>
                  {validationErrors.terms && (
                    <div className="invalid-feedback d-block">{validationErrors.terms}</div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="btn btn-primary d-grid w-100"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  </>
                ) : (
                  'Sign up'
                )}
              </button>
            </form>

            <p className="text-center">
              <span>Already have an account?</span>
              <Link to="/login">
                <span> Sign in instead</span>
              </Link>
            </p>

            <div className="divider my-5">
              <div className="divider-text">or</div>
            </div>

            <div className="d-flex justify-content-center gap-2">
              <a href="javascript:;" className="btn btn-icon rounded-circle btn-text-facebook">
                <i className="tf-icons ri-facebook-fill"></i>
              </a>

              <a href="javascript:;" className="btn btn-icon rounded-circle btn-text-twitter">
                <i className="tf-icons ri-twitter-fill"></i>
              </a>

              <a href="javascript:;" className="btn btn-icon rounded-circle btn-text-github">
                <i className="tf-icons ri-github-fill"></i>
              </a>

              <a href="javascript:;" className="btn btn-icon rounded-circle btn-text-google-plus">
                <i className="tf-icons ri-google-fill"></i>
              </a>
            </div>
          </div>
        </div>
        {/* /Register Form */}
      </div>
    </div>
  );
};

export default Register;